import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { useI18n } from "./i18n-hooks";
import { CLAIM_INVITATION } from "../graphql/invitation";

/**
 * The message for an `InvitationCodeError.messageKey` (`invitation_code_not_found`,
 * `invitation_code_already_used`, `invitation_code_expired`, `already_member`,
 * `account_required`), a generic one for a key not translated yet or no key at all.
 */
export const useInvitationCodeErrorMessage = () => {
    const i18n = useI18n();
    return useCallback(
        (messageKey?: string | null) => {
            const generic = i18n.t("screens.auth.invitation_code.errors.generic");
            return messageKey
                ? i18n.t(`screens.auth.invitation_code.errors.${messageKey}`, {
                      defaultValue: generic,
                  })
                : generic;
        },
        [i18n],
    );
};

export type ClaimInvitationResult =
    | { claimed: true; invitationId: string }
    | { claimed: false; messageKey?: string };

/**
 * Makes the email invitation with that code the signed-in user's (it is then opened by its id like
 * one sent to their phone number). Never throws: a failure answers its `messageKey`, if any.
 */
export const useClaimInvitationCode = () => {
    const [claimInvitation] = useMutation(CLAIM_INVITATION);
    return useCallback(
        async (code: string): Promise<ClaimInvitationResult> => {
            try {
                const { data } = await claimInvitation({ variables: { code } });
                const result = data?.claimInvitation;
                if (result?.__typename === "InvitationCodeSuccess") {
                    return { claimed: true, invitationId: result.invitation.id };
                }
                return { claimed: false, messageKey: result?.messageKey };
            } catch {
                return { claimed: false };
            }
        },
        [claimInvitation],
    );
};
